import { FC, useMemo, useState } from "react";
import {
  Company,
  CompanyMember,
  Role,
  User,
} from "@vviinn/user-managment-client-ts";

import { useAppSelector } from "../../store";
import { useRequestHandlers } from "../../app/hooks/useRequestHandlers";

import { ToggleModalButton } from "../../components/Interaction/Buttons/ToggleModalButton";

import { capitalize } from "../../helpers/string/capitalize";
import { compareAsStrings } from "../../helpers";

import { handleUser } from "../handleUser";
import {
  isGlobalRole,
  isPermitted,
} from "../../helpers/permissions/featurePermission";
import { InviteUserSlideOver } from "./InviteUserSlideOver";
import { MemberByEmailSlideOver } from "./MemberByEmailSlideOver";
import { RoleTable } from "../../ui/layout/molecules/RoleTable";
import { SECTION_TITLE_STYLES } from "../../ui/layout/detailedPages/styles";

type CompanyMembersProps = {
  currentCompany: Company;
};
export const CompanyMembers: FC<CompanyMembersProps> = ({ currentCompany }) => {
  const [openAssignMemberModal, setOpenAssignMemberModal] = useState(false);
  const [openInviteMemberModal, setOpenInviteMemberModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const allUsers = useAppSelector((state) => state.users.users);
  const pages = useAppSelector((state) => state.pages.pages);
  const currentCompanyId = currentCompany.id;
  const actualPermissions = currentCompany.featurePermissions;
  const owners = currentCompany.owners;
  const members = useMemo(() => currentCompany.members ?? [], [currentCompany]);
  const allMembers = useMemo(
    () =>
      owners?.length
        ? [
            ...owners.map((owner) => {
              return {
                ...owner,
                role: "owner",
                name: owner.name ?? owner.email,
              };
            }),
            ...members,
          ]
        : members,
    [members, owners],
  );

  const canEditRoles = useMemo(() => {
    if (!actualPermissions) return false;
    const canEditMembers =
      members.length > 0 &&
      isPermitted(actualPermissions, "company:members:update");
    const canEditOwner = isPermitted(
      actualPermissions,
      "company:owners:update",
    );
    return canEditMembers || canEditOwner;
  }, [actualPermissions, members]);

  // Only global-tier roles can see the full platform user list, so only they get the
  // separate pick-from-a-list assign flow. Company-level owners/editors can't enumerate
  // users, so "assign an existing member" and "invite a new one" collapse into a single
  // Invite user button — the bff resolves by email which of the two it actually is.
  const globalRole = isGlobalRole(pages);

  const unassignedUsers = useMemo(
    () =>
      globalRole
        ? allUsers
            .filter((user) => !allMembers.find((u) => u.id === user.uid))
            .sort((user1, user2) => compareAsStrings(user1.email, user2.email))
        : [],
    [globalRole, allUsers, allMembers],
  );

  const handlerParams = useRequestHandlers();
  const { handleAssignCompanyRole, handleUnassignCompanyRole } =
    handleUser(handlerParams);
  const handleUserUnassign = async (userId: string, role: Role) => {
    if (!currentCompanyId) return;
    await handleUnassignCompanyRole(role, userId, currentCompanyId);
  };

  const assignUser = async (userId: User["uid"], role: Role) => {
    if (!currentCompanyId) return false;
    // Assigning within the current company, so the handler refetches it.
    const requestResult = await handleAssignCompanyRole(
      userId,
      role,
      currentCompanyId,
      currentCompanyId,
    );
    return !!requestResult;
  };

  // Role changes are rare admin/owner actions: lock the whole table while one runs
  // rather than tracking which rows are busy.
  const withLoading = async (action: () => Promise<unknown>) => {
    setLoading(true);
    try {
      await action();
    } finally {
      setLoading(false);
    }
  };

  const userTableData = ({ id, email, role }: CompanyMember) => ({
    cells: [
      <a
        href={`mailto:${email}`}
        className="text-black underline hover:text-stone-900 dark:text-white dark:hover:text-stone-100"
      >
        {email}
      </a>,
      capitalize(role),
    ],
    action: () => withLoading(() => handleUserUnassign(id, role as Role)),
    permissions: `company:${role === "owner" ? "owners" : "members"}:update`,
    upgradeAction:
      role === "viewer"
        ? {
            label: "Upgrade to editor",
            onClick: () => withLoading(() => assignUser(id, "editor")),
            permissions: "company:members:update",
          }
        : undefined,
  });

  return (
    <>
      <section className="grid gap-5">
        <div className="flex items-center gap-2">
          <h2 className={SECTION_TITLE_STYLES}>Members</h2>
          {globalRole && (
            <ToggleModalButton
              buttonTitle="Assign New member"
              createPermissions="user:company:create"
              iconType="newUser"
              onClick={() => setOpenAssignMemberModal(true)}
            />
          )}
          <ToggleModalButton
            buttonTitle="Invite user"
            createPermissions="user:company:create"
            iconType="newUser"
            onClick={() => setOpenInviteMemberModal(true)}
          />
        </div>

        <RoleTable
          thData={["email", "role"]}
          editable={canEditRoles}
          disabled={loading}
          tbData={allMembers?.map((member) => userTableData(member))}
          fallbackMessage="No assigned members"
        />
      </section>

      {globalRole && (
        <InviteUserSlideOver
          users={unassignedUsers}
          open={openAssignMemberModal}
          onClose={() => setOpenAssignMemberModal(false)}
          onAccept={assignUser}
          companyMembers={currentCompany?.members}
        />
      )}

      <MemberByEmailSlideOver
        title="Invite user to the company"
        submitTitle="Send invite"
        open={openInviteMemberModal}
        onClose={() => setOpenInviteMemberModal(false)}
        currentCompanyId={currentCompanyId}
        existingMembers={allMembers}
      />
    </>
  );
};
